import React, { useState, useMemo } from 'react';
import { Box } from '@mui/material';
import SalesActionStatGrid from './SalesActionStatGrid';
import SalesActionStatFilter from './SalesActionStatFilter';
import SalesActionStatDetailCard from './SalesActionStatDetailCard';

const computeDetailedWorkMinutes = (startYear, startMonth, endYear, endMonth, holidayList = []) => {
  if (!startYear || !endYear || !startMonth || !endMonth) return { targetMinutes: 20 * 8 * 60, passedMinutes: 20 * 8 * 60 };

  const startDateObj = new Date(Number(startYear), startMonth - 1, 1);
  const endDateObj = new Date(Number(endYear), endMonth, 0); 
  const today = new Date();

  const todayDateObj = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const effectiveEnd = endDateObj > todayDateObj ? todayDateObj : endDateObj;

  let targetMinutes = 0;   
  let passedMinutes = 0;   

  let current = new Date(startDateObj);

  while (current <= endDateObj) {
    const dayOfWeek = current.getDay();
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      current.setDate(current.getDate() + 1);
      continue;
    }

    const daySetting = holidayList.find(item => item.holidayDate === dateString);
    let dayMinutes = 8 * 60; 

    if (daySetting) {
      if (daySetting.workType === 'FULL') dayMinutes = 0;
      else if (daySetting.workType === 'HALF') dayMinutes = 4 * 60;
    }

    targetMinutes += dayMinutes;

    if (current <= effectiveEnd) {
      passedMinutes += dayMinutes;
    }

    current.setDate(current.getDate() + 1);
  }

  return { targetMinutes, passedMinutes };
};

const SalesActionStat = ({ 
    statEvents = [],
    actionEvents = [],
    holidayList = [],
    dptUuid,
    setDptUuid,
    usrUuid,
    setUsrUuid,
    dptList = [],
    usrList = [],
    onFetchStat
}) => {
    
    const now = new Date();
    const [startYear, setStartYear] = useState(now.getFullYear());
    const [startMonth, setStartMonth] = useState(now.getMonth() + 1);
    const [endYear, setEndYear] = useState(now.getFullYear());
    const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
    const [selectedDetail, setSelectedDetail] = useState(null);

    const { targetWorkMinutes, baseWorkMinutes } = useMemo(() => {
        const { targetMinutes, passedMinutes } = computeDetailedWorkMinutes(startYear, startMonth, endYear, endMonth, holidayList);
        return { targetWorkMinutes: targetMinutes, baseWorkMinutes: passedMinutes };
    }, [startYear, startMonth, endYear, endMonth, holidayList]);

    const activeUserCount = useMemo(() => {
        if (usrUuid) return 1;
        if (!dptUuid) return usrList.length;
        const filteredCount = usrList.filter(usr => usr.dptUuid === dptUuid).length;
        return filteredCount > 0 ? filteredCount : 1;
    }, [dptUuid, usrUuid, usrList]);

    const handleRowClick = (rowData) => {
        setSelectedDetail(rowData);
    };
    return (
        <Box 
            sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                width: '100%', 
                height: '100%',
                gap: 1.5, 
                p: 2,
                backgroundColor: '#ffffff',
                alignItems: 'flex-start' 
            }}
        >
            <Box sx={{ flex: { xs: 'none', md: 3.3 } }}>
                <SalesActionStatFilter 
                    dptUuid={dptUuid} setDptUuid={setDptUuid}
                    usrUuid={usrUuid} setUsrUuid={setUsrUuid}
                    startYear={startYear} setStartYear={setStartYear}
                    startMonth={startMonth} setStartMonth={setStartMonth}
                    endYear={endYear} setEndYear={setEndYear}
                    endMonth={endMonth} setEndMonth={setEndMonth}
                    dptList={dptList}
                    usrList={usrList}
                    onFetchStat={onFetchStat}
                />

                <SalesActionStatGrid 
                    statEvents={statEvents} 
                    actionEvents={actionEvents}
                    holidayList={holidayList}
                    targetWorkMinutes={targetWorkMinutes}
                    baseWorkMinutes={baseWorkMinutes} 
                    startYear={startYear}
                    startMonth={startMonth}
                    endYear={endYear}
                    endMonth={endMonth}
                    userCount={activeUserCount} 
                    onRowClick={handleRowClick}
                /> 
            </Box>
            <Box sx={{ flex: { xs: 'none', md: 1.8 } }}>
                <SalesActionStatDetailCard selectedDetail={selectedDetail} />
            </Box>
        </Box>
    );
};

export default SalesActionStat;
